import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { ACCOUNT_SCOPE_NAMES } from 'common/src/database/scopes/account/accountScopeNames';
import { NESTED_ROUTES } from 'common/src/routes/BaseRoutes';
import { GENERATED_ROUTES } from 'common/src/routes/GeneratedRoutes';
import { IAccount, IPage } from 'common/src/types/interfaces';

import useAuth from 'portal/src//auth/useAuth';
import { getAccounts, updateAccount } from 'portal/src/api/AccountsService';
import { QUERY_KEYS } from 'portal/src/api/constants';
import type { DeactivateConfig } from 'portal/src/components/common/CustomToolbar';
import DataTable from 'portal/src/components/common/DataTable';
import { NEW_ID } from 'portal/src/constants/ReservedIDs';
import { isActiveAccount } from 'portal/src/utils/checkActive';

import buildAccountsColumns from './buildAccountsColumns';

import ViewAccountBills from '../Form/Bills/ViewAccountBills';

const Accounts = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { featureChecker, hasReadOnlyAccess } = useAuth();

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    description: false,
  });
  const [showDeactivatedAccounts, setShowDeactivatedAccounts] =
    useState<boolean>(false);
  const [viewBills, setViewBills] = useState<{
    open: boolean;
    data: Partial<IAccount>;
  }>({ open: false, data: {} });

  const {
    data: accounts,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: [QUERY_KEYS.accounts],
    queryFn: async () =>
      await getAccounts({ scope: ACCOUNT_SCOPE_NAMES.table }),
    initialData: { data: [] } as IPage<IAccount>,
  });

  const deactivateConfig: DeactivateConfig = {
    isChecked: showDeactivatedAccounts,
    onToggleCheckbox: setShowDeactivatedAccounts,
  };

  const handleAddBill = (account: Partial<IAccount>) => {
    navigate(
      `${GENERATED_ROUTES.ADMINISTRATION.BILLS}/${NEW_ID}?account=${account.id}`,
    );
  };

  const handleNavigate = (id: string) => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.detailPageData] });
    navigate(`./${id}`);
  };

  return viewBills.open ? (
    <ViewAccountBills
      accountId={viewBills.data.id}
      handleBackAction={() => setViewBills({ open: false, data: {} })}
    />
  ) : (
    <DataTable<Partial<IAccount>>
      actionsConfig={{
        extraActions: (row) => [
          {
            disabled: hasReadOnlyAccess() || !isActiveAccount(row) || row.isSub,
            icon: <NoteAddOutlinedIcon />,
            label: 'Add Bills',
            onClick: () => handleAddBill(row),
          },
          {
            icon: <DatasetOutlinedIcon />,
            label: 'View Bills',
            onClick: () => setViewBills({ open: true, data: row }),
          },
        ],
        readActionConfig: {
          onClick: (id) => handleNavigate(id),
        },
        changeActiveConfig: {
          targetStr: 'account',
          onChangeActiveStatus: async (id, isActive) => {
            await updateAccount(id, { isActive: !isActive });
            await refetch();
          },
          snackbarMsgKey: 'ACCOUNT',
        },
      }}
      checkboxSelection={false}
      columns={buildAccountsColumns(featureChecker)}
      columnVisibilityModel={columnVisibilityModel}
      contextName={NESTED_ROUTES.ADMINISTRATION.ACCOUNTS}
      dataFetchError={isError}
      loading={isFetching}
      onColumnVisibilityModelChange={setColumnVisibilityModel}
      rows={
        showDeactivatedAccounts
          ? accounts.data
          : accounts.data.filter(({ isActive }) => isActive)
      }
      toolbarSlotProps={{
        deactivateConfig,
        exportFileName: 'accounts',
        onNewButtonClick: () => handleNavigate(NEW_ID),
        showColumns: true,
        showFilters: true,
      }}
    />
  );
};

export default Accounts;
